<template>
    <DelayHydration>
        <section
            class="apartment-list-items"
            v-bind="$attrs"
            :style="{
                '--background-color': backgroundColor,
                '--text-color': textColor
            }"
        >
            <template v-if="!!$slots.header">
                <header class="apartment-list__header">
                    <slot name="header" />
                </header>
            </template>

            <template v-if="pending">
                <ApartmentListItem
                    v-for="i in [1,2,3]"
                    :key="i"
                    :pending="pending"
                />
            </template>
            <template v-else-if="apartments && apartments.length">
                <ApartmentListItem
                    v-for="(apartment, index) in apartments"
                    :key="`apartment-${index}`"
                    :link="'/' + apartment.uri"
                    :apartment="apartment"
                    :images="apartment?.image"
                    :bookable="true"
                    @open-booking-engine="() => {
                        mewsId = apartment?.property?.mewsId;
                        roomId = apartment?.roomId;
                        openBookingEngine();
                    }"
                >
                    <template #type>
                        {{ apartment.title }}
                    </template>

                    <template #description>
                        <h3>{{ apartment.description }}</h3>
                    </template>

                    <template
                        v-if="getAmenities(apartment)?.length > 0"
                        #amenities
                    >
                        <span
                            v-for="(amenity, key) in getAmenities(apartment)"
                            :key="`amenity-${key}`"
                            :title="amenity.title ? amenity.title : amenity.shorthand"
                        >
                            <template v-if="amenity.shorthand">
                                {{ amenity.shorthand }}
                            </template>
                            <template v-else>
                                {{ amenity.title }}
                            </template>
                            <template
                                v-if="key + 1 < getAmenities(apartment).length"
                            >,&nbsp;</template>
                        </span>
                    </template>

                    <template #cta>
                        {{ $t('check-availability') }}
                    </template>

                    <template #link>
                        {{ $t('view-apartment') }}
                    </template>
                </ApartmentListItem>

                <MewsBookingEngine
                    v-if="bookerActive"
                    :form-data="{ date: { start: new Date(), end: new Date()}, guests: {} }"
                    :room-id="roomId"
                    :mews-id="mewsId"
                />
            </template>
        </section>
    </DelayHydration>
</template>

<script setup lang="ts">
import type { ColorPalette } from '~/composables/useColorPalette';
import { useColorPalette } from '~/composables/useColorPalette';

interface Props {
    apartments?: any[];
    backgroundColor?: keyof ColorPalette;
    pending?: boolean;
}

const bookerActive = ref(false);
const mewsId = ref('');
const roomId = ref('');

const props = withDefaults(defineProps<Props>(),
    {
        apartments: () => [],
        pending: false,
        backgroundColor: () => 'lime'
    }
);

const apartments = computed(() => {
    return props.apartments;
});

function getAmenities(apartment: { amenitiesBlock: { amenities: any; }[]; }) {
    let amenities = [];
    const limit = 6;

    amenities = apartment.amenitiesBlock?.[0]?.amenities;

    if (amenities.length >= limit) {
        amenities = amenities.slice(0, limit);
    }

    return amenities;
}

const { resolveColor, getTextColor } = useColorPalette();

const backgroundColor = computed(() => resolveColor(props.backgroundColor));
const textColor = computed(() => getTextColor(props.backgroundColor));

function openBookingEngine() {
    bookerActive.value = false;

    setTimeout(() => {
        bookerActive.value = true;
    }, 50);
}
</script>

<style lang="less">
.apartment-list-items {
    background-color: var(--background-color);
    color: var(--text-color);
    padding-bottom: var(--grid-gutter-margin);

    .apartment-list__content ~ & {
        padding-top: var(--spacing-3xl);
    }

    .apartment-list-item {
        max-width: var(--grid-maxWidth-page);
        margin: auto;
    }

    @media @q-md-max {
        padding-top: var(--spacing-xl);
    }

    @media @q-md-min {
        .apartment-list-item__content {
            padding-bottom: var(--spacing-3xl);
        }
    }
}

.apartment-list__header {
    padding: var(--grid-gutter-width) var(--grid-gutter-margin) var(--spacing-xl);
    text-align: center;
}

.apartment-list-items--spacious {
    .apartment-list-item:not(:last-child) {
        @media @q-md-min {
            padding-bottom: calc(var(--spacing-xl) * 2);
        }
    }
}
</style>
